import { Injectable } from '@angular/core';
import { ModalService } from './modal.service';
import { FilterBarContext, ModalResult } from '../models/modal-config.interface';
import { FiltersSidebarComponent } from 'src/app/shared/components/filters-sidebar/filters-sidebar.component';
import { WrapperModalConfig } from 'src/app/shared/modals/content-modal/content-modal.interface';
import { ModalController } from '@ionic/angular/standalone';
import { FilterResult, FiltersConfig, FilterValues } from 'src/app/shared/models/filter.interface';
import { Subject, Observable, debounceTime } from 'rxjs';
import { UserService } from './user.service';
import { ApiService } from './api.service';
import { PermissionsEnum, RolesEnum } from '../constants/roles-and-permissions.enum';
import { DictionaryDTO } from '../models/dictionary';

const SYNC_APP_SETTINGS_DEBOUNCE_MS = 400;

@Injectable({ providedIn: 'root' })
export class FiltersModalService {
  private resultSubject = new Subject<ModalResult<FilterResult>>();
  private currentComponent: FiltersSidebarComponent;
  private filterBarContext: FilterBarContext | null = null;
  private readonly syncAppSettings$ = new Subject<void>();
  private lastSyncedAppSettingsKey: string | null = null;

  constructor(
    private readonly modalService: ModalService,
    private readonly modalController: ModalController,
    private readonly user: UserService,
    private readonly api: ApiService
  ) {
    this.syncAppSettings$
      .pipe(debounceTime(SYNC_APP_SETTINGS_DEBOUNCE_MS))
      .subscribe(() => this.flushSyncAppSettings());
  }

  registerFilterBarContext(context: FilterBarContext): void {
    this.filterBarContext = context;
  }

  unregisterFilterBarContext(): void {
    this.filterBarContext = null;
  }

  removeChip(key: string): void {
    this.filterBarContext?.onChipRemove(key);
  }

  openFilterPanelByChip(scrollToKey: string): void {
    this.filterBarContext?.onChipClick(scrollToKey);
  }

  openFilter(config: FiltersConfig): Observable<ModalResult<FilterResult>> {
    const wrapperConfig: WrapperModalConfig = {
      title: config.title,
      headerButton: {
        visible: true,
        label: 'Сбросить',
        severity: 'secondary',
        variant: 'text',
        componentMethod: () => this.resetFilters()
      },
      footerButtons: [
        {
          visible: true,
          label: 'Показать',
          severity: 'primary',
          componentMethod: () => this.applyFilters()
        }
      ]
    };

    this.modalService.setWrapperConfig(wrapperConfig);

    this.modalService.createWithDynamicComponent({
      component: FiltersSidebarComponent,
      componentProps: {
        config: {
          ...config,
          onApply: (filters: FilterValues, count: number) => this.handleApplyFromComponent(filters, count),
          onReset: () => {},
          onComponentReady: (component: FiltersSidebarComponent) => this.currentComponent = component
        }
      }
    }).subscribe();

    return this.resultSubject.asObservable();
  }

  private handleApplyFromComponent(filters: FilterValues, count: number): void {
    this.resultSubject.next({
      role: 'saved',
      data: { filters: filters || {}, count: count || 0 }
    });

    this.modalController.dismiss();
    this.clear();
  }

  private applyFilters(filters?: FilterValues, count?: number): void {
    if (!filters && this.currentComponent) {
      this.currentComponent.onApplyFilters();
      return;
    }

    this.handleApplyFromComponent(filters, count);
  }

  private resetFilters(): void {
    if (this.currentComponent && typeof this.currentComponent.onResetFilters === 'function') {
      this.currentComponent.onResetFilters();
    }
  }

  private clear(): void {
    this.currentComponent = null;
    this.resultSubject = new Subject<ModalResult<FilterResult>>();
  }

  canUsePvzScopeFilters(): boolean {
    return (
      this.user.data.role == RolesEnum.ROLE_OWNER ||
      this.user.checkPermissions([
        PermissionsEnum.PERM_PVZS_READ_ATTACHED,
        PermissionsEnum.PERM_PVZS_READ_ALL,
        PermissionsEnum.PERM_PVZS_MANAGE_ATTACHED,
        PermissionsEnum.PERM_PVZS_MANAGE_ALL,
      ])
    );
  }

  canUseMoneyPayMethodsFilters(): boolean {
    return (
      this.user.data.role == RolesEnum.ROLE_OWNER ||
      this.user.checkPermissions([
        PermissionsEnum.PERM_MONEY_MANAGE_ATTACHED,
        PermissionsEnum.PERM_MONEY_MANAGE_ALL,
      ])
    );
  }

  /**
   * Применяет выбранные группы и организации к фильтрам
   * @param filters - фильтры
   * @returns фильтры с выбранными группами и организациями
   */
  applyUserSelectedScopeToFilters(filters: FilterValues): FilterValues {
    const nextFilters = { ...(filters || {}) };
    const rawSelectedGroup = this.user.data.settings.appSettings.selected_group;
    const rawSelectedOrganization = this.user.data.settings.appSettings.selected_organization;

    if (nextFilters.pvz_group_id == null && rawSelectedGroup) {
      const selectedGroup = typeof rawSelectedGroup === 'string' ? JSON.parse(rawSelectedGroup) : rawSelectedGroup;
      if (selectedGroup?.id && selectedGroup.id < 0) {
        const groupId = -Number(selectedGroup.id);
        if (Number.isInteger(groupId) && groupId > 0) {
          nextFilters.pvz_group_id = groupId;
        }
      }
    }

    if (nextFilters.organization_id == null && rawSelectedOrganization) {
      const selectedOrganization =
        typeof rawSelectedOrganization === 'string' ? JSON.parse(rawSelectedOrganization) : rawSelectedOrganization;
      const organizationId = Number(selectedOrganization?.id);
      if (Number.isInteger(organizationId) && organizationId > 0) {
        nextFilters.organization_id = organizationId;
      }
    }

    return nextFilters;
  }

  /**
   * Синхронизирует выбранные группы и организации с фильтрами
   * @param filters - фильтры
   * @param pvzGroupsDict - словарь групп ПВЗ
   * @param organizationsDict - словарь организаций
   */
  syncUserSelectedScopeByFilters(
    filters: FilterValues,
    pvzGroupsDict: DictionaryDTO[] = [],
    organizationsDict: DictionaryDTO[] = [],
  ): void {
    const nextFilters = filters || {};
    let hasChanges = false;

    const groupId = Number(nextFilters.pvz_group_id);
    const hasGroup = Number.isInteger(groupId) && groupId > 0;
    const rawSelectedGroup = this.user.data.settings.appSettings.selected_group;
    const selectedGroup = typeof rawSelectedGroup === 'string' ? JSON.parse(rawSelectedGroup || 'null') : rawSelectedGroup;

    if (!hasGroup) {
      if (rawSelectedGroup) {
        this.user.data.settings.appSettings.selected_group = null;
        hasChanges = true;
      }
    } else if (Number(selectedGroup?.id) !== -groupId) {
      const groupName = pvzGroupsDict.find((item) => Number(item.id) === groupId)?.name;
      this.user.data.settings.appSettings.selected_group = JSON.stringify({
        id: -groupId,
        name: groupName ? `Группа ПВЗ: ${groupName}` : `Группа ПВЗ: ${groupId}`,
      });
      hasChanges = true;
    }

    const organizationId = Number(nextFilters.organization_id);
    const hasOrganization = Number.isInteger(organizationId) && organizationId > 0;
    const rawSelectedOrganization = this.user.data.settings.appSettings.selected_organization;
    const selectedOrganization =
      typeof rawSelectedOrganization === 'string' ? JSON.parse(rawSelectedOrganization || 'null') : rawSelectedOrganization;

    if (!hasOrganization) {
      if (rawSelectedOrganization) {
        this.user.data.settings.appSettings.selected_organization = null;
        hasChanges = true;
      }
    } else if (Number(selectedOrganization?.id) !== organizationId) {
      const organizationName = organizationsDict.find((item) => Number(item.id) === organizationId)?.name;
      this.user.data.settings.appSettings.selected_organization = JSON.stringify({
        id: organizationId,
        name: organizationName ? `Организация: ${organizationName}` : `Организация: ${organizationId}`,
      });
      hasChanges = true;
    }

    if (hasChanges) {
      this.scheduleSyncAppSettings();
    }
  }

  /**
   * Планирует синхронизацию appSettings с debounce и пропуском
   * повторных запросов с тем же payload.
   */
  private scheduleSyncAppSettings(): void {
    this.user.saveUserData();
    this.syncAppSettings$.next();
  }

  private flushSyncAppSettings(): void {
    const appSettings = this.user.data.settings.appSettings;
    const syncKey = [
      appSettings?.selected_group ?? '',
      appSettings?.selected_organization ?? '',
    ].join('|');

    if (syncKey === this.lastSyncedAppSettingsKey) {
      return;
    }

    this.lastSyncedAppSettingsKey = syncKey;
    this.api.syncUserData('appSettings');
  }
}
